import { Box, Grid, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import {
  FormAwareDatePickerInput,
  FormAwareDatePickerInputProps,
} from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import {
  validateAtMost100Percent,
  ValidatedInputRepeaterPercentSumRow,
} from '@/components/form/formAwareInputs/FormAwareInputRepeater/ValidatedInputRepeaterPercentSumRow';
import { FormAwareMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareMultiSelectInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { Loader } from '@/components/progress/Loader/Loader';
import { useFormContext } from '@/components/react-hook-form';
import { LocatableFormAwareInput } from '@/modules/aiSuggestions/components/forms/LocatableFormAwareInput';
import { SuggestableFormAwareInput } from '@/modules/aiSuggestions/components/forms/SuggestableFormAwareInput';
import { useAISuggestionsEnabled } from '@/modules/aiSuggestions/hooks/useAISuggestionsEnabled';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import {
  AddableType,
  AddNewAnythingModal,
} from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { AiSuggestionKind } from '@/types/schema';
import { getComparableDecimalJS } from '@/utils/decimalJSUtils';

import { useIsShortForm } from '../EntityShortFormModal/CreateEntityShortForm/context/CreateEntityShortForm.context';
import { OWNERSHIP_SYNC_SUPPORTED_INTEGRATIONS } from './BusinessEntityDetailsSubform.constants';
import {
  BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE,
  BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE,
  Fields,
  Props,
  SubformField,
} from './BusinessEntityDetailsSubform.types';
import { KEY_PERSON_ROLE_KIND_ITEMS } from './BusinessEntityDetailsSubform.utils';
import { useFetchBusinessEntityDetailsOptions } from './useGetBusinessEntityOwnerOptions';

type AddFromSelection = 'owners' | 'keyPeople';

export function UI(props: Props) {
  const { householdId, entityId, entityType } = props;
  const { control, setValue } = useFormContext<Fields>();
  const isShortFormVariant = useIsShortForm();
  const shouldGroupDuplicates = useAISuggestionsEnabled(entityType);
  const { duplicatesRefs } = useEditEntitySplitScreenModalContext();

  const [addFromSelection, setAddFromSelection] =
    useState<AddFromSelection | null>(null);

  const addableTypesFromSelection: AddableType[] = useMemo(() => {
    switch (addFromSelection) {
      case 'keyPeople':
        return ['individual', 'organization', 'entity'];
      case 'owners':
        return ['entity', 'individual'];
      default:
        return [];
    }
  }, [addFromSelection]);

  const subformValues = useWatch({
    name: BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE,
    control,
  });

  const { selectedOwnerIds, selectedKeyPersonIds } = useMemo(() => {
    return {
      selectedOwnerIds: subformValues?.owners?.map((o) => o.ownerId) ?? [],
      selectedKeyPersonIds:
        subformValues?.keyPeople?.map((kp) => kp.keyPersonId) ?? [],
    };
  }, [subformValues]);

  const ownershipSyncIntegrationKind =
    subformValues?._ownershipSyncIntegrationKind;
  const isOwnershipSyncSupported =
    ownershipSyncIntegrationKind &&
    OWNERSHIP_SYNC_SUPPORTED_INTEGRATIONS.has(ownershipSyncIntegrationKind);

  const {
    ownerOptions,
    keyPeopleOptions,
    loading: formOptionsLoading,
  } = useFetchBusinessEntityDetailsOptions(
    householdId,
    entityId ?? null,
    selectedOwnerIds,
    selectedKeyPersonIds
  );

  const ownerships = useWatch({
    name: `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners`,
    control,
  });

  const ownershipSum = (ownerships || []).reduce((acc, o) => {
    acc = acc.add(o.percentOwned ?? new Decimal(0));
    return acc;
  }, new Decimal(0));
  useEffect(() => {
    setValue(
      `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}._ownershipSum` as const,
      ownershipSum
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // getComparableDecimalJS is the only way to avoid infinite setValues when the value
    // is a Decimal.js instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getComparableDecimalJS(
      ownershipSum,
      BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE
    ),
    setValue,
  ]);

  if (formOptionsLoading) {
    return (
      <Loader
        boxProps={{
          sx: {
            textAlign: 'center',
            my: 3,
          },
        }}
      />
    );
  }

  return (
    <>
      <AddNewAnythingModal
        isOpen={addableTypesFromSelection.length > 0}
        addableTypes={addableTypesFromSelection}
        handleClose={() => setAddFromSelection(null)}
        householdId={householdId}
      />
      <FormFieldsDisabledProvider isDisabled={formOptionsLoading}>
        <Grid container spacing={2} columns={12}>
          <Grid item sm={12}>
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <FormAwareInputRepeater<Fields>
                  name={`${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners`}
                  emptyValue={{
                    ownerId: '',
                    percentOwned: null,
                  }}
                  control={control}
                  shouldInit
                  addAnotherItemText={`Add additional owner`}
                  rightFooterContent={
                    <ValidatedInputRepeaterPercentSumRow<Fields>
                      decimalScale={BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE}
                      validationField={
                        `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}._ownershipSum` as const satisfies SubformField
                      }
                      validation={{
                        totalValue: (value) =>
                          validateAtMost100Percent(value as Decimal | null),
                      }}
                    />
                  }
                  render={(
                    i: number,
                    _props,
                    _id,
                    additionalItemRendererProps
                  ) => {
                    const hideLabel =
                      additionalItemRendererProps?.hideLabelOverride ?? i > 0;

                    return (
                      <FormLayoutRow>
                        <FormLayoutItem width={8}>
                          <LocatableFormAwareInput<
                            Fields,
                            FormAwareTypeaheadSelectInputProps<Fields>
                          >
                            FormAwareElement={FormAwareTypeaheadSelectInput}
                            suggestionKind={AiSuggestionKind.BeneficialOwner}
                            fieldName={
                              `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners.${i}.ownerId` as const satisfies SubformField
                            }
                            label={`Beneficial owners`}
                            hideLabel={isShortFormVariant ? true : hideLabel}
                            control={control}
                            options={ownerOptions}
                            disabled={formOptionsLoading}
                            required={true}
                            placeholder="Search beneficial owners"
                            groupBy={(option) => option.groupName ?? ''}
                            addNewOption={{
                              onClick: () => setAddFromSelection('owners'),
                            }}
                            additionalItemRendererProps={
                              additionalItemRendererProps
                            }
                          />
                        </FormLayoutItem>
                        <FormLayoutItem width={4} sx={{ alignSelf: 'end' }}>
                          <FormAwarePercentInput<Fields>
                            fieldName={
                              `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners.${i}.percentOwned` as const satisfies SubformField
                            }
                            label={'Percentage'}
                            hideLabel={isShortFormVariant ? true : hideLabel}
                            control={control}
                            decimalScale={
                              BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE
                            }
                            isDecimalJSInput
                            required={true}
                          />
                        </FormLayoutItem>
                      </FormLayoutRow>
                    );
                  }}
                  idField="ownerId"
                  shouldGroupDuplicates={shouldGroupDuplicates}
                  duplicatesRef={
                    duplicatesRefs[
                      `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners`
                    ]
                  }
                />
              </FormLayoutItem>
            </FormLayoutRow>
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <Grid container spacing={4}>
                  <Grid item sm={isOwnershipSyncSupported ? 6 : 12}>
                    <Box sx={{ maxWidth: 200 }}>
                      <SuggestableFormAwareInput<
                        Fields,
                        FormAwareDatePickerInputProps<Fields>
                      >
                        FormAwareElement={FormAwareDatePickerInput}
                        control={control}
                        fieldName={
                          `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.ownershipAsOfDate` as const satisfies SubformField
                        }
                        label="As of date"
                        required={true}
                        suggestionKind={
                          AiSuggestionKind.BeneficialOwnersAsOfDate
                        }
                      />
                    </Box>
                  </Grid>
                  {isOwnershipSyncSupported && (
                    <Grid item sm={6}>
                      <Typography variant="title-xs" sx={{ mb: 1 }}>
                        {getIntegrationProviderDisplayName(
                          ownershipSyncIntegrationKind
                        )}{' '}
                        sync
                      </Typography>
                      <FormAwareCheckbox
                        control={control}
                        fieldName={
                          `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.syncOwnership` as const satisfies SubformField
                        }
                        label="Keep beneficial owners synced"
                        contextualHelp={
                          <PopperContent
                            body="When enabled, ownership information will be
                              automatically synchronized and kept current based
                              on the latest available data."
                          />
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </FormLayoutItem>
            </FormLayoutRow>
            {!isShortFormVariant && (
              <>
                <FormLayoutRow>
                  <FormLayoutItem width={12}>
                    <FormAwareInputRepeater<Fields>
                      name={`${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople`}
                      emptyValue={{
                        keyPersonId: '',
                        roles: [],
                      }}
                      control={control}
                      shouldInit
                      addAnotherItemText={`Add additional key person`}
                      render={(
                        i: number,
                        _props,
                        _id,
                        additionalItemRendererProps
                      ) => {
                        const hideLabel =
                          additionalItemRendererProps?.hideLabelOverride ??
                          i > 0;

                        return (
                          <FormLayoutRow>
                            <FormLayoutItem width={8}>
                              <LocatableFormAwareInput<
                                Fields,
                                FormAwareTypeaheadSelectInputProps<Fields>
                              >
                                FormAwareElement={FormAwareTypeaheadSelectInput}
                                suggestionKind={AiSuggestionKind.KeyPerson}
                                fieldName={
                                  `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople.${i}.keyPersonId` as const satisfies SubformField
                                }
                                label="Key people"
                                hideLabel={hideLabel}
                                control={control}
                                options={keyPeopleOptions}
                                disabled={formOptionsLoading}
                                placeholder="Search key people"
                                groupBy={(option) => option.groupName ?? ''}
                                addNewOption={{
                                  onClick: () =>
                                    setAddFromSelection('keyPeople'),
                                }}
                                additionalItemRendererProps={
                                  additionalItemRendererProps
                                }
                              />
                            </FormLayoutItem>
                            <FormLayoutItem width={4} sx={{ alignSelf: 'end' }}>
                              <FormAwareMultiSelectInput<Fields>
                                fieldName={
                                  `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople.${i}.roles` as const satisfies SubformField
                                }
                                label="Roles"
                                hideLabel={hideLabel}
                                control={control}
                                options={KEY_PERSON_ROLE_KIND_ITEMS}
                              />
                            </FormLayoutItem>
                          </FormLayoutRow>
                        );
                      }}
                      idField="keyPersonId"
                      shouldGroupDuplicates={shouldGroupDuplicates}
                      duplicatesRef={
                        duplicatesRefs[
                          `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople`
                        ]
                      }
                    />
                  </FormLayoutItem>
                </FormLayoutRow>
                <FormLayoutRow>
                  <FormLayoutItem>
                    <FormAwareTextInput<Fields>
                      control={control}
                      label="Notes on key people"
                      fieldName={
                        `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeopleNotes` as const satisfies SubformField
                      }
                      multiline
                      rows={2}
                      resizable
                    />
                  </FormLayoutItem>
                </FormLayoutRow>
              </>
            )}
          </Grid>
        </Grid>
      </FormFieldsDisabledProvider>
    </>
  );
}
